import { computed, ref, shallowRef } from 'vue'
import { defineStore } from 'pinia'
import { authApi } from '@/services/auth.api'
import { platformApi } from '@/services/platform.api'
import { configureHttp } from '@/services/http'
import { toAppError } from '@/services/errors'
import { grants, grantsAny, SCOPES } from '@/constants/permissions'
import { setLocale, currentLocale } from '@/i18n'

/**
 * Dueña de la sesión: token en memoria, identidad, permisos y suplantación.
 *
 * Decisiones:
 *  - El token de acceso NO se guarda en `localStorage`. Vive 15 minutos y el
 *    refresh token está en una cookie httpOnly; al recargar la página se pide
 *    uno nuevo con `/auth/refresh`. Un XSS no se lleva nada persistente.
 *  - Los permisos vienen íntegros de `/auth/me`. El panel no deduce permisos
 *    a partir del rol: si el backend no lo concede, aquí no existe.
 */
export const useSessionStore = defineStore('session', () => {
  const accessToken = ref(null)
  const user = shallowRef(null)
  const organization = shallowRef(null)
  const role = ref(null)
  const scope = ref(null)
  const permissions = ref([])
  const impersonation = shallowRef(null)

  /**
   * Organizaciones a las que este usuario puede cambiar por membresía propia
   * (`[{ organizationId, name, role }]`). Llegan de `/auth/me` ya filtradas y
   * ordenadas: el panel no decide a qué organizaciones se puede entrar.
   */
  const memberships = ref([])

  /** `unknown` mientras no se ha resuelto el arranque: evita parpadeos de login. */
  const bootstrapped = ref(false)
  const isBootstrapping = ref(false)
  const bootstrapError = shallowRef(null)

  /** Token del usuario de plataforma guardado mientras dura una suplantación. */
  let tokenBeforeImpersonation = null
  let sessionLostHandler = () => {}

  /**
   * ¿Puede esta sesión volver a la consola de plataforma?
   *
   * Lo responde `/auth/me` en TODOS los ámbitos: dentro de una organización el
   * ámbito activo ya no delata que el usuario sea además de plataforma, y sin
   * este campo el operador entraría en su organización sin puerta de vuelta.
   * Durante una suplantación llega `false` a propósito — con la suplantación
   * abierta el backend rechaza el cambio de organización, y la salida es
   * terminarla, no esquivarla.
   */
  const platformAccess = ref(false)

  const isAuthenticated = computed(() => Boolean(accessToken.value && user.value))
  const isPlatform = computed(() => scope.value === SCOPES.PLATFORM)
  const isImpersonating = computed(() => Boolean(impersonation.value))

  /**
   * ¿Tiene sentido ofrecer el selector de organización?
   *
   * Con una sola opción posible no se muestra nada: un selector de un elemento
   * es ruido. Y durante una suplantación tampoco: ahí manda la barra de
   * suplantación y tener dos formas de cambiar de organización a la vez es la
   * mejor manera de que alguien acabe en la equivocada. Esa guarda no se
   * contradice con `platformAccess` — que también es `false` suplantando —,
   * cubre además el caso de un usuario con varias membresías propias, que
   * mientras suplanta tampoco puede cambiarse de organización.
   */
  const canSwitchOrganization = computed(() => {
    if (isImpersonating.value) return false
    const count = memberships.value.length
    return platformAccess.value ? count >= 1 : count > 1
  })

  /**
   * Ámbito efectivo. Un usuario de plataforma que suplanta se comporta, dentro
   * de `/app`, como un usuario de la organización suplantada.
   */
  const effectiveScope = computed(() =>
    isImpersonating.value ? SCOPES.ORGANIZATION : (scope.value ?? SCOPES.ORGANIZATION),
  )

  const displayName = computed(() => user.value?.name || user.value?.email || '')

  /** @param {string} permission */
  function can(permission) {
    return grants(permissions.value, permission)
  }

  /** @param {string[]} required @param {'any'|'all'} mode */
  function canAny(required, mode = 'any') {
    return grantsAny(permissions.value, required, mode)
  }

  function applyIdentity(payload = {}) {
    user.value = payload.user ?? null
    organization.value = payload.organization ?? null
    role.value = payload.role ?? null
    scope.value = payload.scope ?? (payload.organization ? SCOPES.ORGANIZATION : SCOPES.PLATFORM)
    permissions.value = Array.isArray(payload.permissions) ? payload.permissions : []
    impersonation.value = payload.impersonation ?? null
    memberships.value = Array.isArray(payload.memberships) ? payload.memberships : []

    // Si un extremo antiguo no mandara el campo, se cae al ámbito activo: una
    // sesión de plataforma evidentemente puede usar la consola de plataforma.
    platformAccess.value =
      typeof payload.platformAccess === 'boolean'
        ? payload.platformAccess
        : scope.value === SCOPES.PLATFORM && !impersonation.value

    // La pestaña anota el ámbito que el servidor acaba de confirmar, nunca el
    // que se pidió. Suplantando no se anota nada: el ámbito es prestado y la
    // pestaña sigue siendo la de la sesión de plataforma que hay debajo.
    if (user.value && !impersonation.value) {
      const active = scope.value === SCOPES.PLATFORM ? null : organization.value?.id
      if (active !== undefined) rememberTabScope(active)
    }

    // El idioma del usuario manda sobre el del navegador.
    const preferred = payload.user?.locale?.slice(0, 2)
    if (preferred && preferred !== currentLocale()) setLocale(preferred)
  }

  /**
   * Adopta la identidad que venga en la respuesta de `login` o de
   * `switch-organization` — el contrato la anida en `me`, pero se acepta
   * también plana — y, si no viene completa, la pide a `/auth/me`.
   */
  async function adoptIdentity(payload = {}) {
    const identity = payload.me ?? payload
    if (identity?.user && Array.isArray(identity.permissions)) applyIdentity(identity)
    else await loadMe()
  }

  function clearIdentity() {
    accessToken.value = null
    user.value = null
    organization.value = null
    role.value = null
    scope.value = null
    permissions.value = []
    impersonation.value = null
    memberships.value = []
    platformAccess.value = false
    tokenBeforeImpersonation = null
    forgetTabScope()
  }

  /** Acepta las formas razonables de respuesta sin acoplarse a una sola. */
  function extractToken(payload = {}) {
    return payload.accessToken ?? payload.token ?? payload.tokens?.accessToken ?? null
  }

  /* ─── Ciclo de vida ─────────────────────────────────────────────────────── */

  /**
   * Arranque de la aplicación: intenta canjear la cookie de refresco por un
   * token de acceso. Si no hay cookie válida, la sesión queda anónima y el
   * router llevará al login. Nunca lanza: el arranque no puede romperse.
   */
  async function bootstrap() {
    if (bootstrapped.value || isBootstrapping.value) return
    isBootstrapping.value = true
    bootstrapError.value = null

    try {
      // Se manda el ámbito que esta pestaña tenía antes de recargar; el
      // servidor lo honra sólo si el usuario tiene derecho a él y, si no, lo
      // ignora. La identidad que vale es la que responda `/auth/me`.
      const payload = await authApi.refresh(readTabScope())
      const token = extractToken(payload)
      if (!token) throw new Error('sin token')
      accessToken.value = token
      await loadMe()
    } catch (caught) {
      const error = toAppError(caught)
      // 401 = simplemente no hay sesión. Cualquier otra cosa sí es un fallo real.
      if (error.status !== 401 && error.status !== 403) bootstrapError.value = error
      clearIdentity()
    } finally {
      isBootstrapping.value = false
      bootstrapped.value = true
    }
  }

  async function loadMe() {
    const payload = await authApi.me()
    applyIdentity(payload)
    return payload
  }

  /** @param {{ email: string, password: string }} credentials */
  async function login(credentials) {
    const payload = await authApi.login(credentials)
    const token = extractToken(payload)
    if (!token) throw new Error('La respuesta de login no incluye token de acceso')
    accessToken.value = token
    await adoptIdentity(payload)

    bootstrapped.value = true
    return payload
  }

  /**
   * Cambia el ámbito de la sesión a una organización propia, o vuelve a la
   * consola de plataforma con `null`.
   *
   * No es una suplantación: el usuario entra por una membresía legítima y no
   * debe quedar registrado como acceso de soporte. El token nuevo se queda en
   * memoria, igual que el resto; lo que persiste es la cookie de refresco, que
   * el backend rota, y por eso el cambio sobrevive a una recarga.
   *
   * @param {string|null} organizationId
   */
  async function switchOrganization(organizationId = null) {
    const payload = await authApi.switchOrganization(organizationId ?? null)
    const token = extractToken(payload)
    if (!token) throw new Error('La respuesta de cambio de organización no incluye token')

    accessToken.value = token
    await adoptIdentity(payload)
    return payload
  }

  async function logout() {
    try {
      await authApi.logout()
    } catch {
      // Cerrar sesión en el cliente no puede depender de que la red responda.
    } finally {
      clearIdentity()
    }
  }

  /**
   * Hook del interceptor HTTP. Devuelve el token nuevo o `null`.
   *
   * Manda el ámbito de la pestaña por la misma razón que el arranque: sin él,
   * dos pestañas con ámbitos distintos se pisan y la primera que renueve manda
   * sobre las dos.
   *
   * Si el servidor no honró la pista y devuelve otro ámbito, se adopta el que
   * venga: seguir pintando el anterior sería mentir con la interfaz. No se
   * pide `/auth/me` desde aquí a propósito — esto se ejecuta dentro del
   * refresco *single-flight* del cliente HTTP, y una petición que necesitara
   * refrescar se quedaría esperando a este mismo refresco.
   */
  async function refreshTokens() {
    const payload = await authApi.refresh(readTabScope())
    const token = extractToken(payload)
    accessToken.value = token

    const identity = payload?.me
    if (token && identity?.user && Array.isArray(identity.permissions)) applyIdentity(identity)

    return token
  }

  /* ─── Suplantación ──────────────────────────────────────────────────────── */

  /**
   * Entra a la organización de un cliente como soporte. El motivo es
   * obligatorio y queda en `impersonation_sessions`.
   * @param {string} organizationId
   * @param {string} reason
   */
  async function impersonate(organizationId, reason) {
    const payload = await platformApi.impersonate(organizationId, reason)
    const token = extractToken(payload)
    if (!token) throw new Error('La respuesta de suplantación no incluye token')

    tokenBeforeImpersonation = accessToken.value
    accessToken.value = token
    await loadMe()

    // Si el backend no marca la suplantación en `/auth/me`, se marca aquí:
    // la barra de aviso no puede depender de un campo opcional.
    if (!impersonation.value) {
      impersonation.value = {
        organization: payload.organization ?? organization.value,
        reason,
        expiresAt: payload.expiresAt ?? null,
      }
    }
    return payload
  }

  /**
   * Sale de la suplantación y vuelve a la sesión de plataforma.
   *
   * `docs/API.md` no define una ruta para terminar una suplantación, así que se
   * recupera la sesión original en este orden: (1) el token de plataforma que
   * se guardó al entrar; (2) si ya venció, `/auth/refresh`, porque la cookie de
   * refresco sigue siendo la del usuario de plataforma.
   */
  async function stopImpersonation() {
    const previous = tokenBeforeImpersonation
    tokenBeforeImpersonation = null
    impersonation.value = null

    if (previous) {
      accessToken.value = previous
      try {
        await loadMe()
        return
      } catch {
        // El token guardado venció: se sigue por la vía del refresco.
      }
    }

    try {
      const token = await refreshTokens()
      if (!token) throw new Error('sin token')
      await loadMe()
    } catch {
      clearIdentity()
      sessionLostHandler()
    }
  }

  /** El router instala aquí su redirección al login. */
  function onSessionLost(handler) {
    sessionLostHandler = handler
  }

  // Enganches del cliente HTTP. Se inyectan en vez de importarse para no crear
  // un ciclo entre la capa de servicios y el store.
  configureHttp({
    getAccessToken: () => accessToken.value,
    refresh: () => refreshTokens(),
    getLocale: () => currentLocale(),
    onSessionLost: () => {
      clearIdentity()
      sessionLostHandler()
    },
  })

  return {
    accessToken,
    user,
    organization,
    role,
    scope,
    permissions,
    impersonation,
    memberships,
    bootstrapped,
    isBootstrapping,
    bootstrapError,
    platformAccess,
    isAuthenticated,
    isPlatform,
    isImpersonating,
    canSwitchOrganization,
    effectiveScope,
    displayName,
    can,
    canAny,
    bootstrap,
    loadMe,
    login,
    logout,
    refreshTokens,
    switchOrganization,
    impersonate,
    stopImpersonation,
    onSessionLost,
  }
})

/* ─── Memoria de ámbito de la pestaña ──────────────────────────────────────── */

/**
 * El ámbito activo de ESTA pestaña.
 *
 * No es una pista de permisos ni concede nada: el backend la honra sólo si el
 * usuario tiene membresía real (o rol de plataforma para `null`) y, si no, la
 * ignora en silencio y resuelve el ámbito por derecho propio. Sirve para que
 * cada pestaña conserve el suyo al renovar el token — sin ella, con la consola
 * de plataforma abierta en una pestaña y el panel de un cliente en otra, la
 * primera que renueve arrastra a la otra.
 *
 * Vive en `sessionStorage` porque es exactamente eso: memoria de una pestaña,
 * que muere con ella. Se anota siempre con lo que el servidor confirmó, nunca
 * con lo que se pidió, y se borra al cerrar sesión.
 */
const TAB_SCOPE_KEY = 'iris.tabScope'

/** Marca del ámbito de plataforma: `sessionStorage` sólo guarda cadenas. */
const PLATFORM_MARK = 'platform'

/**
 * @returns {string|null|undefined} id de organización, `null` para plataforma,
 * o `undefined` si esta pestaña todavía no tiene ámbito propio.
 */
function readTabScope() {
  try {
    const stored = sessionStorage.getItem(TAB_SCOPE_KEY)
    if (stored === null) return undefined
    return stored === PLATFORM_MARK ? null : stored
  } catch {
    return undefined
  }
}

/** @param {string|null} organizationId `null` para el ámbito de plataforma */
function rememberTabScope(organizationId) {
  try {
    sessionStorage.setItem(TAB_SCOPE_KEY, organizationId ?? PLATFORM_MARK)
  } catch {
    /* almacenamiento no disponible */
  }
}

function forgetTabScope() {
  try {
    sessionStorage.removeItem(TAB_SCOPE_KEY)
  } catch {
    /* almacenamiento no disponible */
  }
}
